import type { IsoDate, IsoDateTime } from './types.ts';

/** Fuseau de référence de la section (Belgique). */
export const SECTION_TIMEZONE = 'Europe/Brussels';

export function toDate(value: IsoDate | IsoDateTime | Date): Date {
  if (value instanceof Date) return value;
  // Une date civile nue est interprétée à midi UTC pour éviter qu'un décalage
  // de fuseau ne fasse basculer l'anniversaire sur le jour précédent.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(`${value}T12:00:00Z`);
  return new Date(value);
}

export function isoDate(value: Date | IsoDateTime): IsoDate {
  const d = value instanceof Date ? value : new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function nowIso(): IsoDateTime {
  return new Date().toISOString();
}

/**
 * Âge en années révolues à la date de référence.
 * Renvoie `null` si la date de naissance est absente ou invalide.
 */
export function ageAt(birthDate: IsoDate | null | undefined, at: Date = new Date()): number | null {
  if (!birthDate) return null;
  const birth = toDate(birthDate);
  if (Number.isNaN(birth.getTime())) return null;
  let age = at.getFullYear() - birth.getFullYear();
  const monthDiff = at.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && at.getDate() < birth.getDate())) age -= 1;
  return age < 0 ? null : age;
}

/**
 * Nombre de jours jusqu'au prochain anniversaire (0 = aujourd'hui).
 * Gère le 29 février en le ramenant au 1er mars les années non bissextiles.
 */
export function daysUntilBirthday(birthDate: IsoDate | null | undefined, at: Date = new Date()): number | null {
  if (!birthDate) return null;
  const birth = toDate(birthDate);
  if (Number.isNaN(birth.getTime())) return null;

  const startOfToday = new Date(at.getFullYear(), at.getMonth(), at.getDate());
  const buildFor = (year: number) => {
    const month = birth.getUTCMonth();
    const day = birth.getUTCDate();
    const candidate = new Date(year, month, day);
    // Le 29/02 d'une année non bissextile devient le 01/03.
    if (candidate.getMonth() !== month) return new Date(year, month + 1, 1);
    return candidate;
  };

  let next = buildFor(startOfToday.getFullYear());
  if (next < startOfToday) next = buildFor(startOfToday.getFullYear() + 1);
  return Math.round((next.getTime() - startOfToday.getTime()) / 86_400_000);
}

/** Année scolaire scoute au format `2025-2026` (bascule au 1er septembre). */
export function schoolYearOf(at: Date | IsoDateTime = new Date()): string {
  const d = toDate(at);
  const year = d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1;
  return `${year}-${year + 1}`;
}

/**
 * Première année scolaire de Bala'App. Les sélecteurs (finances, présences…)
 * ne proposent jamais d'année antérieure : l'application démarre en 2026–2027.
 */
export const FIRST_SCHOOL_YEAR = '2026-2027';

/** Année de début (`2026` pour `2026-2027`). */
export function schoolYearStartYear(period: string): number {
  return Number(period.slice(0, 4));
}

/** Bornes civiles d'une année scolaire : 1er septembre → 31 août. */
export function schoolYearDateBounds(period: string): { from: IsoDate; to: IsoDate } {
  const year = schoolYearStartYear(period);
  return { from: `${year}-09-01`, to: `${year + 1}-08-31` };
}

/**
 * Année scolaire « active » pour l'UI : l'année calendaire en cours, jamais
 * avant `FIRST_SCHOOL_YEAR`. Avant le 1er septembre 2026, on reste donc sur
 * 2026–2027 plutôt que de retomber sur 2025–2026.
 */
export function activeSchoolYear(at: Date | IsoDateTime = new Date()): string {
  const current = schoolYearOf(at);
  return schoolYearStartYear(current) < schoolYearStartYear(FIRST_SCHOOL_YEAR)
    ? FIRST_SCHOOL_YEAR
    : current;
}

/**
 * Années scolaires proposées dans les sélecteurs : de `FIRST_SCHOOL_YEAR`
 * jusqu'à l'année en cours (la plus récente en tête). Les années suivantes
 * s'ajoutent seules à chaque bascule du 1er septembre.
 */
export function schoolYearOptions(at: Date | IsoDateTime = new Date()): string[] {
  const first = schoolYearStartYear(FIRST_SCHOOL_YEAR);
  const current = Math.max(schoolYearStartYear(schoolYearOf(at)), first);
  const years: string[] = [];
  for (let year = current; year >= first; year -= 1) {
    years.push(`${year}-${year + 1}`);
  }
  return years;
}

/**
 * Années civiles proposées pour une campagne d'uniformes (référence de paiement).
 * De la première année Bala'App jusqu'à l'année en cours + 1 (préparer la
 * rentrée suivante), la plus récente en tête.
 */
export function campaignYearOptions(at: Date | IsoDateTime = new Date()): number[] {
  const first = schoolYearStartYear(FIRST_SCHOOL_YEAR);
  const current = Math.max(toDate(at).getFullYear(), first);
  const last = current + 1;
  const years: number[] = [];
  for (let year = last; year >= first; year -= 1) {
    years.push(year);
  }
  return years;
}

export function startOfDay(value: Date | IsoDateTime): Date {
  const d = toDate(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function endOfDay(value: Date | IsoDateTime): Date {
  const d = startOfDay(value);
  d.setDate(d.getDate() + 1);
  d.setMilliseconds(-1);
  return d;
}

/** Lundi de la semaine contenant `value` (semaine ISO, commence lundi). */
export function startOfWeek(value: Date | IsoDateTime): Date {
  const d = startOfDay(value);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d;
}

export function startOfMonth(value: Date | IsoDateTime): Date {
  const d = toDate(value);
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function addDays(value: Date | IsoDateTime, days: number): Date {
  const d = toDate(value);
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

export function addMonths(value: Date | IsoDateTime, months: number): Date {
  const d = toDate(value);
  const out = new Date(d.getFullYear(), d.getMonth() + months, 1);
  return out;
}

export function isSameDay(a: Date | IsoDateTime, b: Date | IsoDateTime): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

/**
 * Grille de 6 semaines (42 jours) couvrant le mois, du lundi au dimanche.
 * Format attendu par la vue mensuelle du calendrier.
 */
export function monthGrid(value: Date | IsoDateTime): Date[] {
  const first = startOfWeek(startOfMonth(value));
  return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

// ---------------------------------------------------------------------------
// Formatage (français, fuseau de la section)
// ---------------------------------------------------------------------------

const dateFmt = new Intl.DateTimeFormat('fr-BE', { dateStyle: 'long' });
const shortDateFmt = new Intl.DateTimeFormat('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('fr-BE', { hour: '2-digit', minute: '2-digit' });
const weekdayFmt = new Intl.DateTimeFormat('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' });
const monthFmt = new Intl.DateTimeFormat('fr-BE', { month: 'long', year: 'numeric' });

export const formatDate = (v: Date | IsoDateTime) => dateFmt.format(toDate(v));
export const formatShortDate = (v: Date | IsoDateTime) => shortDateFmt.format(toDate(v));
export const formatTime = (v: Date | IsoDateTime) => timeFmt.format(toDate(v));
export const formatWeekday = (v: Date | IsoDateTime) => weekdayFmt.format(toDate(v));
export const formatMonth = (v: Date | IsoDateTime) => monthFmt.format(toDate(v));

export function formatDateTime(v: Date | IsoDateTime): string {
  const d = toDate(v);
  return `${formatDate(d)} à ${formatTime(d)}`;
}

/** Plage horaire compacte : « sam. 14 mars, 14:00 – 17:00 ». */
export function formatRange(start: Date | IsoDateTime, end: Date | IsoDateTime, allDay = false): string {
  const s = toDate(start);
  const e = toDate(end);
  if (allDay) {
    return isSameDay(s, e) ? formatWeekday(s) : `${formatWeekday(s)} → ${formatWeekday(e)}`;
  }
  if (isSameDay(s, e)) return `${formatWeekday(s)}, ${formatTime(s)} – ${formatTime(e)}`;
  return `${formatWeekday(s)} ${formatTime(s)} → ${formatWeekday(e)} ${formatTime(e)}`;
}

/** « dans 3 jours », « il y a 2 h », « à l'instant ». */
export function formatRelative(v: Date | IsoDateTime, at: Date = new Date()): string {
  const target = toDate(v);
  const diffMs = target.getTime() - at.getTime();
  const abs = Math.abs(diffMs);
  const minutes = Math.round(abs / 60_000);

  if (minutes < 1) return "à l'instant";
  const past = diffMs < 0;
  const wrap = (text: string) => (past ? `il y a ${text}` : `dans ${text}`);

  if (minutes < 60) return wrap(`${minutes} min`);
  const hours = Math.round(minutes / 60);
  if (hours < 24) return wrap(`${hours} h`);
  const days = Math.round(hours / 24);
  if (days === 1) return past ? 'hier' : 'demain';
  if (days < 7) return wrap(`${days} jours`);
  const weeks = Math.round(days / 7);
  if (weeks < 5) return wrap(weeks === 1 ? '1 semaine' : `${weeks} semaines`);
  const months = Math.round(days / 30);
  if (months < 12) return wrap(months === 1 ? '1 mois' : `${months} mois`);
  const years = Math.round(days / 365);
  return wrap(years === 1 ? '1 an' : `${years} ans`);
}

/** Durée en minutes → « 1 h 30 ». */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`;
}

/** Valeur pour un `<input type="datetime-local">` (heure locale, sans fuseau). */
export function toDateTimeLocalValue(v: Date | IsoDateTime): string {
  const d = toDate(v);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromDateTimeLocalValue(value: string): IsoDateTime {
  return new Date(value).toISOString();
}
